import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api').replace(/\/+$/, '');

export const normalizeMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (typeof window === 'undefined') return url;
  
  // If it's a base64 string, return as is
  if (url.startsWith('data:')) return url;

  // If it's a relative path, prepend API base URL
  let finalUrl = url;
  if (url.startsWith('/uploads/') || url.startsWith('uploads/')) {
    const relativePath = url.startsWith('/') ? url : `/${url}`;
    finalUrl = `${API_BASE_URL.replace('/api', '')}${relativePath}`;
  }

  try {
    const current = window.location;
    const parsed = new URL(finalUrl, current.origin);
    
    // Only force protocol for http/https URLs, not for data: or others
    if (parsed.protocol.startsWith('http') && parsed.protocol !== current.protocol) {
      // Ensure we don't break stream URLs which might need specific protocol
      // But generally matching current protocol avoids mixed content
      parsed.protocol = current.protocol;
      return parsed.toString();
    }
    return parsed.toString();
  } catch (e) {
    // Fallback for non-URL strings or failed parsing
    // If it looks like a relative path but failed URL parsing, try constructing absolute
    if (finalUrl.startsWith('/') && typeof window !== 'undefined') {
        return `${window.location.origin}${finalUrl}`;
    }
    return finalUrl;
  }
};

export const getVideos = async () => {
  const res = await axios.get(`${API_BASE_URL}/videos`);
  const list = Array.isArray(res.data) ? res.data : [];
  return list.map((v) => ({
    ...v,
    videoSource: normalizeMediaUrl(v.videoSource)
  }));
};

export const getVideoUrl = async (id) => {
  if (!id) return null;
  const res = await axios.get(`${API_BASE_URL}/videos`);
  const list = Array.isArray(res.data) ? res.data : [];
  const found = list.find((v) => v.id === id || v.id === String(id));
  if (!found || !found.videoSource) {
    return null;
  }
  const src = normalizeMediaUrl(found.videoSource);
  return src;
};

export const saveVideo = async (video) => {
  const hasFile = !!video.videoFile;
  const baseFields = {
    title: video.title || '',
    desc: video.desc || '',
    category: video.category || '',
    duration: video.duration || '',
    views: video.views || '',
    date: video.date || '',
    thumbnail: video.thumbnail || '',
    tags: Array.isArray(video.tags) ? video.tags : []
  };

  if (hasFile) {
    const formData = new FormData();
    Object.keys(baseFields).forEach((key) => {
      formData.append(key, baseFields[key]);
    });
    if (video.id) {
      formData.append('id', video.id);
    }
    formData.append('video', video.videoFile);

    const res = await axios.post(`${API_BASE_URL}/videos/upload`, formData, {
      headers: {
        // Let axios set the content-type with boundary
      },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      timeout: 0
    });
    return res.data;
  }

  const payload = {
    ...baseFields,
    videoSource: video.videoSource || '',
    hasLocalVideo: !!video.videoSource
  };

  if (video.id) {
    const res = await axios.put(`${API_BASE_URL}/videos/${video.id}`, payload);
    return res.data;
  }

  const res = await axios.post(`${API_BASE_URL}/videos`, payload);
  return res.data;
};

export const deleteVideo = async (id) => {
  if (!id) return;
  await axios.delete(`${API_BASE_URL}/videos/${id}`);
};

export const getVideoFile = async (id) => {
  if (!id) return null;
  const res = await axios.get(`${API_BASE_URL}/videos`);
  const list = (Array.isArray(res.data) ? res.data : []).map((v) => ({
    ...v,
    videoSource: normalizeMediaUrl(v.videoSource)
  }));
  const found = list.find((v) => v.id === id || v.id === String(id));
  if (!found || !found.videoSource) {
    return null;
  }
  const src = normalizeMediaUrl(found.videoSource);
  const response = await fetch(src);
  const blob = await response.blob();
  return blob;
};

export const saveVideoFile = saveVideo;
export const deleteVideoFile = deleteVideo;
